/**
 * ahri.ts — AHRI-01 (Seth 2026-09-25: "Under jobs > equipment & Accessories, is
 * there a way to enter an AHRI number. Is there anyway that if you need it could
 * go out and match the equipment and select that properly from the equipment in
 * pegasus? Fill in the properly SEER2 ratings, etc. If not … can we at least log
 * it and save it as part of a job so we can view it later").
 *
 * One row per AHRI certified reference number on an install job
 * (pegasus_job_ahri) — a job can carry two systems, and a furnace has its own
 * AHRI number apart from the AC / heat-pump match.
 *
 * Where the ratings come from:
 *   · ENERGY STAR's public list (data.energystar.gov dataset 83eb-xbyy — public
 *     domain, no key) carries the AHRI number, the matched models and SEER2 /
 *     EER2 / HSPF2 for heat pumps, mini-splits and heat pump + furnace matches.
 *     Pegasus looks the number up there.
 *   · ENERGY STAR stopped listing central air conditioners on Feb 1, 2026 and
 *     never listed furnaces by AHRI number, so an AC match or a furnace is typed
 *     off the AHRI certificate (attach the certificate as a job file).
 *   · AHRI's own directory data is licensed: its site says the data may not be
 *     copied into software or databases. Pegasus links to the directory's page
 *     for the number and never copies from it. (A full lookup for every system
 *     = AHRI's paid data subscription — Seth's call.)
 *
 * Competitor frame: ServiceTitan / FieldEdge / Jobber / Housecall Pro keep the
 * number in a custom field + the certificate PDF; the AHRI matching lives with
 * the tools that pay for AHRI data (distributor sites, OnCall Air).
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type AhriSystemType = "ac" | "heat_pump" | "mini_split" | "furnace" | "other";
export const AHRI_TYPES: AhriSystemType[] = ["ac", "heat_pump", "mini_split", "furnace", "other"];
export const AHRI_TYPE_LABEL: Record<AhriSystemType, string> = {
  ac: "AC System",
  heat_pump: "Heat Pump System",
  mini_split: "Mini-Split",
  furnace: "Furnace",
  other: "Other",
};

export interface JobAhri {
  id: string;
  job_id: string;
  ahri_ref: string;
  system_type: AhriSystemType | null;
  brand: string | null;
  series: string | null;
  outdoor_model: string | null;
  indoor_model: string | null;
  furnace_model: string | null;
  seer2: number | null;
  eer2: number | null;
  hspf2: number | null;
  cooling_btuh: number | null;
  heating_btuh: number | null;
  afue: number | null;
  cold_climate: boolean | null;
  source: "energy_star" | "typed";
  looked_up_at: string | null;
  note: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** What the form edits (the row minus its keys and stamps). */
export type AhriDraft = Omit<JobAhri, "id" | "job_id" | "created_by" | "created_at" | "updated_at">;

export function emptyAhriDraft(ref = ""): AhriDraft {
  return {
    ahri_ref: ref, system_type: null, brand: null, series: null,
    outdoor_model: null, indoor_model: null, furnace_model: null,
    seer2: null, eer2: null, hspf2: null, cooling_btuh: null, heating_btuh: null, afue: null,
    cold_climate: null, source: "typed", looked_up_at: null, note: null,
  };
}

/** AHRI numbers are digits — people paste them with spaces or dashes. */
export const cleanAhriRef = (s: string) => s.replace(/\D/g, "");

/** The AHRI directory's own page for the number. Verified for heat pumps
 *  (/details/99/<#>); air conditioners are the directory's program 101.
 *  Furnaces / unknown → the directory's home page, whose top bar searches by
 *  AHRI number. */
export function ahriLink(ref: string, type: AhriSystemType | null): string {
  const r = cleanAhriRef(ref);
  if (type === "heat_pump" || type === "mini_split") return `https://www.ahridirectory.org/details/99/${r}`;
  if (type === "ac") return `https://www.ahridirectory.org/details/101/${r}`;
  return "https://www.ahridirectory.org/";
}

// ── Look it up on ENERGY STAR's public list ────────────────────────────────
const ES_HEAT_PUMPS = "https://data.energystar.gov/resource/83eb-xbyy.json";

export type AhriLookup =
  | { status: "found"; draft: AhriDraft }
  | { status: "not_found" }
  | { status: "error"; message: string };

export async function lookupAhri(ref: string): Promise<AhriLookup> {
  const r = cleanAhriRef(ref);
  if (r.length < 5) return { status: "error", message: "An AHRI number is digits only — usually 7 to 10 of them." };
  let rows: Array<Record<string, string>>;
  try {
    const res = await fetch(`${ES_HEAT_PUMPS}?ahri_reference_number=${encodeURIComponent(r)}&$limit=1`);
    if (!res.ok) return { status: "error", message: `ENERGY STAR's list didn't answer (error ${res.status}). Type the ratings off the certificate, or try again.` };
    rows = (await res.json()) as Array<Record<string, string>>;
  } catch {
    return { status: "error", message: "Couldn't reach ENERGY STAR's list. Type the ratings off the certificate, or try again." };
  }
  const x = rows[0];
  if (!x) return { status: "not_found" };
  const num = (v: string | undefined) => (v == null || v === "" || Number.isNaN(Number(v)) ? null : Number(v));
  const kind = `${x.product_type ?? ""}`.toLowerCase();
  return {
    status: "found",
    draft: {
      ahri_ref: r,
      system_type: /mini|ductless/.test(kind) ? "mini_split" : "heat_pump",
      brand: x.outdoor_unit_brand_name || x.brand_name || null,
      series: x.series_name || null,
      outdoor_model: x.model_number || null,
      indoor_model: x.indoor_unit_model_number || null,
      furnace_model: x.furnace_model_number || null,
      seer2: num(x.seer2_btu_wh),
      eer2: num(x.eer2_btu_wh),
      hspf2: num(x.hspf2_btu_wh),
      cooling_btuh: num(x.cooling_capacity_btu_h),
      heating_btuh: num(x.heating_capacity_at_47_f_btu_h),
      afue: null,
      cold_climate: x.cold_climate ? /^y/i.test(x.cold_climate) : null,
      source: "energy_star",
      looked_up_at: new Date().toISOString(),
      note: null,
    },
  };
}

// ── Matching the certificate's models to the job + the pricebook ───────────
/** Uppercase, no spaces / dashes / slashes / dots. */
export const normModel = (s: string | null | undefined) => (s ?? "").toUpperCase().replace(/[\s\-_/.]/g, "");

/** The part of a listed model before any wildcard or option group
 *  ("A97UH2E045B12S*" → "A97UH2E045B12S", "EAC4X30B+TXV" → "EAC4X30B"). */
export function modelStem(listed: string | null | undefined): string {
  return normModel((listed ?? "").split(/[*#?(+]/)[0]);
}

/** Does a model on the job match the model on the certificate? The data tag
 *  often carries extra characters after the listed model, and lists use
 *  wildcards — so a prefix match on the stem, at least 5 characters. */
export function modelMatches(listed: string | null | undefined, onJob: string | null | undefined): boolean {
  const stem = modelStem(listed);
  const j = normModel(onJob);
  if (stem.length < 5 || j.length < 5) return false;
  return j.startsWith(stem) || (stem.startsWith(j) && j.length >= 7);
}

export interface PricebookHit {
  id: string;
  part_number: string | null;
  description: string | null;
  category: string | null;
  manufacturer: string | null;
  model: string | null;
  units: string | null;
  average_cost: number | null;
  last_purchase: number | null;
  qty_in_stock: number | null;
}

/** Pricebook items whose model or part number starts like the listed model. */
export function usePricebookByModel(listed: string | null | undefined) {
  const raw = (listed ?? "").split(/[*#?(+]/)[0].replace(/[^A-Za-z0-9-]/g, "");
  return useQuery({
    queryKey: ["ahri_pricebook", raw.toUpperCase()],
    enabled: raw.replace(/-/g, "").length >= 5,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<PricebookHit[]> => {
      const { data, error } = await supabase
        .from("item")
        .select("id, part_number, description, category, manufacturer, model, units, average_cost, last_purchase, qty_in_stock")
        .or(`model.ilike.${raw}%,part_number.ilike.${raw}%`)
        .or("archived.is.null,archived.eq.false")
        // PRICEBOOK-SEARCH-01 rule: inactive items (the "Do not use" copies) never offer themselves.
        .not("inactive", "is", true)
        .limit(5);
      if (error) throw error;
      return ((data ?? []) as PricebookHit[]).filter((h) => modelMatches(listed, h.model) || modelMatches(listed, h.part_number));
    },
  });
}

// ── The job's AHRI rows ───────────────────────────────────────────────────
export function useJobAhri(jobId: string | null | undefined) {
  return useQuery({
    queryKey: ["job_ahri", jobId],
    enabled: !!jobId,
    queryFn: async (): Promise<JobAhri[]> => {
      const { data, error } = await supabase.from("pegasus_job_ahri").select("*").eq("job_id", jobId!).order("created_at");
      if (error) throw error;
      return ((data ?? []) as JobAhri[]).map((r) => ({
        ...r,
        seer2: r.seer2 == null ? null : Number(r.seer2),
        eer2: r.eer2 == null ? null : Number(r.eer2),
        hspf2: r.hspf2 == null ? null : Number(r.hspf2),
        cooling_btuh: r.cooling_btuh == null ? null : Number(r.cooling_btuh),
        heating_btuh: r.heating_btuh == null ? null : Number(r.heating_btuh),
        afue: r.afue == null ? null : Number(r.afue),
      }));
    },
  });
}

export function useSaveAhri() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id?: string; jobId: string; draft: AhriDraft }) => {
      const ref = cleanAhriRef(v.draft.ahri_ref);
      if (ref.length < 5) throw new Error("An AHRI number is digits only — usually 7 to 10 of them.");
      const x = v.draft;
      const row = {
        ahri_ref: ref, job_id: v.jobId, system_type: x.system_type, brand: x.brand, series: x.series,
        outdoor_model: x.outdoor_model?.trim() || null, indoor_model: x.indoor_model?.trim() || null, furnace_model: x.furnace_model?.trim() || null,
        seer2: x.seer2, eer2: x.eer2, hspf2: x.hspf2, cooling_btuh: x.cooling_btuh, heating_btuh: x.heating_btuh, afue: x.afue,
        cold_climate: x.cold_climate, source: x.source, looked_up_at: x.looked_up_at, note: x.note?.trim() || null,
        updated_at: new Date().toISOString(),
      };
      if (v.id) {
        const { error } = await supabase.from("pegasus_job_ahri").update(row).eq("id", v.id);
        if (error) throw friendly(error);
      } else {
        const { data: u } = await supabase.auth.getUser();
        const { error } = await supabase.from("pegasus_job_ahri").insert({ ...row, created_by: u?.user?.email ?? null });
        if (error) throw friendly(error);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job_ahri"] }),
  });
}

export function useDeleteAhri() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pegasus_job_ahri").delete().eq("id", id);
      if (error) throw friendly(error);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job_ahri"] }),
  });
}

function friendly(e: { code?: string; message?: string }): Error {
  if (e.code === "23505") return new Error("That AHRI number is already on this job.");
  return new Error(e.message ?? "Couldn't save the AHRI number.");
}

/** "18.0 SEER2 · 12.3 EER2 · 8.1 HSPF2" — the ratings that are filled in. */
export function ratingText(r: Pick<JobAhri, "seer2" | "eer2" | "hspf2" | "afue">): string {
  const f = (n: number) => (Number.isInteger(n) ? n.toFixed(1) : String(n));
  return [
    r.seer2 != null ? `${f(r.seer2)} SEER2` : null,
    r.eer2 != null ? `${f(r.eer2)} EER2` : null,
    r.hspf2 != null ? `${f(r.hspf2)} HSPF2` : null,
    r.afue != null ? `${r.afue}% AFUE` : null,
  ].filter(Boolean).join(" · ");
}
